import { Box, Button, CircularProgress, IconButton, Menu, MenuItem, Pagination, Popover, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AppDispatch, RootState } from '../../redux/store/appStore';
import { getReview } from '../../redux/slices/reviewerSlice';
import { getExpertAnalytics, getExpertAnalyticsDownload, setDownloadAnalyticsByExpertStatus } from '../../redux/slices/analytics/experts';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { AnalyticsGranularity, getDateRangeForGranularity } from '../../utils/helper';
import Error from '../error/error';
import { STATUS } from '../../utils/status';
import DownloadIcon from '@mui/icons-material/Download';
import SnackbarNotification from '../snackbar/snackbarNotification';
import { usePollingTaskStatus } from '../outputJson/usePollingStatus';
import TaskStatusModal from '../outputJson/taskStatusModal';
import { getOutputJsonTaskStatus, setTaskStatus } from '../../redux/slices/outputJson';

const PAGE_SIZE = 10;

const TABLE_COLUMN_CONFIG = [
  { label: 'VENDOR', sortField: 'vendor_name', align: 'left', minWidth: 160 },
  { label: 'TOTAL TASKS', sortField: 'total_tasks', align: 'center', minWidth: 110 },
  { label: 'COMPLETED', sortField: 'completed', align: 'center', minWidth: 105 },
  { label: 'PENDING', sortField: 'pending', align: 'center', minWidth: 100 },
  { label: 'ACTIVE TASKS', sortField: 'active_tasks', align: 'center', minWidth: 110 },
  { label: 'ACCEPTED AS-IS (COUNT)', sortField: 'quality_accepted_as_is_tasks', align: 'center', minWidth: 150 },
  { label: 'ACCEPTED AS-IS (%)', sortField: 'quality_accepted_as_is_percentage', align: 'center', minWidth: 130 },
  { label: 'ACCEPTED WITH EDITS (COUNT)', sortField: 'quality_accepted_with_edits_tasks', align: 'center', minWidth: 170 },
  { label: 'ACCEPTED WITH EDITS (%)', sortField: 'quality_accepted_with_edits_percentage', align: 'center', minWidth: 150 },
  { label: 'REJECTED (COUNT)', sortField: 'quality_rejected_tasks', align: 'center', minWidth: 130 },
  { label: 'REJECTED (%)', sortField: 'quality_rejected_percentage', align: 'center', minWidth: 110 },
  { label: 'ACCEPTED (TOTAL)', sortField: 'quality_accepted_tasks', align: 'center', minWidth: 130 },
  { label: 'AWAITING REVIEW TASKS', sortField: 'awaiting_review_tasks', align: 'center', minWidth: 160 },
  { label: 'COMPLETION %', sortField: 'completion_percentage', align: 'center', minWidth: 120 },
  { label: 'QUALITY SCORE', sortField: 'quality_score', align: 'center', minWidth: 120 },
] as const;

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toPercentage = (numerator: number, denominator: number): number => {
  if (denominator <= 0) {
    return 0;
  }
  return Number(((numerator / denominator) * 100).toFixed(2));
};

const VendorAnalytics = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams();
  const association_type = useSelector((state: RootState) => state.projects.association_type);
  const reviewStages = useSelector((state: RootState) => state.reviewStage.reviewer?.results || []);
  const stageOptions = useMemo(() => {
    return reviewStages.filter((stage: any) => {
      const stageCategory = String(stage?.stage_type || stage?.category || '').toUpperCase();
      return stageCategory === 'TASK';
    });
  }, [reviewStages]);
  const vendorAnalyticsData = useSelector((state: RootState) => state.analyticsExpert.analyticsExpert);
  const vendorAnalyticsStatus = useSelector((state: RootState) => state.analyticsExpert.getStatus);
  const { downloadAnalyticsByExpertStatus, analyticsExpertDownload, getExpertAnalyticsDownloadMessage } = useSelector((state: RootState) => state.analyticsExpert);
  const { getTaskStatusOutputJson, getTaskStatusOutputJsonMessage, taskStatusOutputJsonResponse } = useSelector((state: RootState) => state.outputJson);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStageId, setSelectedStageId] = useState('');
  const dayDateRange = useMemo(() => getDateRangeForGranularity('day'), []);
  const [granularity, setGranularity] = useState<AnalyticsGranularity>('day');
  const [appliedStartDate, setAppliedStartDate] = useState(dayDateRange.startDate);
  const [appliedEndDate, setAppliedEndDate] = useState(dayDateRange.endDate);
  const [sortBy, setSortBy] = useState('vendor_name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState<HTMLElement | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [openTaskStatusModal, setOpenTaskStatusModal] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(getReview({ project_id: id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (!id || association_type !== 'COMPANY' || !appliedStartDate || !appliedEndDate) {
      return;
    }

    dispatch(
      getExpertAnalytics({
        project_id: id,
        association_type,
        tab: 'vendors',
        granularity,
        start_date: appliedStartDate || undefined,
        end_date: appliedEndDate || undefined,
        search: searchQuery || undefined,
        stage_id: selectedStageId || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        page: currentPage,
        pageSize: PAGE_SIZE,
      })
    );
  }, [dispatch, id, association_type, searchQuery, selectedStageId, sortBy, sortOrder, currentPage, granularity, appliedStartDate, appliedEndDate]);

  useEffect(() => {
    const debounceTimer = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setCurrentPage(1);
    }, 300);

    return () => {
      window.clearTimeout(debounceTimer);
    };
  }, [searchInput]);

  useEffect(() => {
    if (!selectedStageId) {
      return;
    }

    const hasSelectedStage = stageOptions.some((stage: any) => String(stage.id) === selectedStageId);
    if (!hasSelectedStage) {
      setSelectedStageId('');
    }
  }, [selectedStageId, stageOptions]);

  const rows = useMemo(() => {
    return vendorAnalyticsData?.results || [];
  }, [vendorAnalyticsData]);

  const totalPages = Math.max(1, Math.ceil(Number(vendorAnalyticsData?.count || 0) / PAGE_SIZE));

  const taskId =
    downloadAnalyticsByExpertStatus === STATUS.SUCESS &&
      analyticsExpertDownload &&
      typeof analyticsExpertDownload === 'object' &&
      'task_id' in analyticsExpertDownload
      ? (analyticsExpertDownload as { task_id: string }).task_id
      : undefined;

  usePollingTaskStatus({
    status: taskStatusOutputJsonResponse?.data?.status,
    taskId,
  });

  useEffect(() => {
    const status = taskStatusOutputJsonResponse?.data?.status;
    if (status === 'PENDING' || status === 'SUCCESS' || status === 'FAILURE') {
      setOpenTaskStatusModal(true);
    }
  }, [taskStatusOutputJsonResponse]);

  useEffect(() => {
    if (downloadAnalyticsByExpertStatus === STATUS.SUCESS) {
      setSnackbarMessage(getExpertAnalyticsDownloadMessage || 'Vendor analytics export started.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      if (taskId) {
        dispatch(getOutputJsonTaskStatus({ celery_task_id: taskId }));
      }
    }

    if (downloadAnalyticsByExpertStatus === STATUS.FAILED) {
      setSnackbarMessage('Could not start vendor analytics export. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }

    return () => {
      if (downloadAnalyticsByExpertStatus === STATUS.SUCESS || downloadAnalyticsByExpertStatus === STATUS.FAILED) {
        dispatch(setDownloadAnalyticsByExpertStatus(STATUS.NOT_STARTED));
      }
    };
  }, [dispatch, downloadAnalyticsByExpertStatus, getExpertAnalyticsDownloadMessage, taskId]);

  useEffect(() => {
    if (getTaskStatusOutputJson === STATUS.SUCESS && taskStatusOutputJsonResponse?.data?.url) {
      const url = taskStatusOutputJsonResponse.data.url;
      setSnackbarMessage('Vendor analytics export is ready for download.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      if (typeof url === 'string') {
        fetch(url)
          .then((response) => response.blob())
          .then((blob) => {
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = url.split('/').pop() || 'vendor-analytics-export';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
          })
          .catch(() => {
            setSnackbarMessage('Failed to download vendor analytics export.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          });
      }
    }

    if (getTaskStatusOutputJson === STATUS.FAILED) {
      setSnackbarMessage(getTaskStatusOutputJsonMessage || 'Vendor analytics export failed.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }

    return () => {
      if (getTaskStatusOutputJson === STATUS.SUCESS || getTaskStatusOutputJson === STATUS.FAILED) {
        dispatch(setTaskStatus(STATUS.NOT_STARTED));
      }
    };
  }, [dispatch, getTaskStatusOutputJson, getTaskStatusOutputJsonMessage, taskStatusOutputJsonResponse]);

  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadMenuClose = (): void => {
    setDownloadAnchorEl(null);
  };

  const handleDownloadVendorAnalytics = (): void => {
    dispatch(
      getExpertAnalyticsDownload({
        project_id: id,
        association_type,
        tab: 'vendors',
        type: 'VENDOR',
        search: searchQuery || undefined,
        stage_id: selectedStageId || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        granularity,
        start_date: appliedStartDate || undefined,
        end_date: appliedEndDate || undefined,
      })
    );
    handleDownloadMenuClose();
  };

  const renderValue = (value: unknown): string | number => {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    return value as string | number;
  };

  const headerCellSx = {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '10px',
    lineHeight: '14px',
    color: '#2D333A',
    letterSpacing: '0.01em',
    py: 1.2,
  };

  const bodyCellSx = {
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: '12px',
    color: '#27374A',
    py: 1,
  };

  const handleColumnSort = (sortField: string): void => {
    if (sortBy === sortField) {
      setSortOrder((previousOrder) => (previousOrder === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(sortField);
      setSortOrder('asc');
    }

    setCurrentPage(1);
  };

  const getSplitMetrics = (row: any) => {
    const acceptedAsIsCount = toNumber(
      row?.quality_accepted_as_is_tasks ??
      row?.total_quality_accepted_as_is ??
      row?.accepted_as_is_count ??
      0
    );
    const acceptedWithEditsCount = toNumber(
      row?.quality_accepted_with_edits_tasks ??
      row?.total_quality_accepted_with_edits ??
      row?.accepted_with_edits_count ??
      0
    );
    const rejectedCount = toNumber(
      row?.quality_rejected_tasks ??
      row?.total_quality_rejected ??
      row?.rejected_count ??
      0
    );
    const reviewedTotal = toNumber(row?.total_reviewed ?? acceptedAsIsCount + acceptedWithEditsCount + rejectedCount);

    const acceptedAsIsPercentage = toNumber(
      row?.quality_accepted_as_is_percentage ??
      row?.accepted_as_is_percentage ??
      toPercentage(acceptedAsIsCount, reviewedTotal)
    );
    const acceptedWithEditsPercentage = toNumber(
      row?.quality_accepted_with_edits_percentage ??
      row?.accepted_with_edits_percentage ??
      toPercentage(acceptedWithEditsCount, reviewedTotal)
    );
    const rejectedPercentage = toNumber(
      row?.quality_rejected_percentage ??
      row?.rejected_percentage ??
      toPercentage(rejectedCount, reviewedTotal)
    );

    return {
      acceptedAsIsCount,
      acceptedAsIsPercentage,
      acceptedWithEditsCount,
      acceptedWithEditsPercentage,
      rejectedCount,
      rejectedPercentage,
      acceptedTotal: acceptedAsIsCount + acceptedWithEditsCount,
    };
  };

  return (
    <>
      {association_type === 'COMPANY' &&
        (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
              <Typography sx={{ marginLeft: '10px', fontFamily: 'Montserrat', fontWeight: 700, fontSize: '20px', lineHeight: '14px' }}>
                Vendor Analytics
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={granularity}
                  onChange={(_event, value: AnalyticsGranularity | null) => {
                    if (!value) return;
                    const dateRange = getDateRangeForGranularity(value);
                    setGranularity(value);
                    setAppliedStartDate(dateRange.startDate);
                    setAppliedEndDate(dateRange.endDate);
                    setCurrentPage(1);
                  }}
                  sx={{
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontSize: '12px',
                      padding: '4px 10px',
                    },
                  }}
                >
                  <ToggleButton value="day">Day</ToggleButton>
                  <ToggleButton value="week">Week</ToggleButton>
                  <ToggleButton value="month">Month</ToggleButton>
                </ToggleButtonGroup>
                <Tooltip title="Filter" placement="bottom-end" arrow>
                  <IconButton
                    aria-label="filter options"
                    onClick={(event) => setFilterAnchorEl(event.currentTarget)}
                    size="small"
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      color: 'white',
                      backgroundColor: '#3D85C6',
                      '&:hover': { backgroundColor: '#2F6EA8' },
                    }}
                  >
                    <FilterAltIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Download" placement="bottom-end" arrow>
                  <IconButton
                    onClick={handleDownloadMenuOpen}
                    size="small"
                    disabled={downloadAnalyticsByExpertStatus === STATUS.PENDING}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      color: 'white',
                      backgroundColor: downloadAnalyticsByExpertStatus === STATUS.PENDING ? '#9ca3af' : '#3D85C6',
                      '&:hover': { backgroundColor: downloadAnalyticsByExpertStatus === STATUS.PENDING ? '#9ca3af' : '#2F6EA8' },
                    }}
                  >
                    {downloadAnalyticsByExpertStatus === STATUS.PENDING ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <DownloadIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Menu
              anchorEl={downloadAnchorEl}
              open={Boolean(downloadAnchorEl)}
              onClose={handleDownloadMenuClose}
            >
              <MenuItem onClick={handleDownloadVendorAnalytics}>Download Vendor Analytics</MenuItem>
            </Menu>

            <Popover
              open={Boolean(filterAnchorEl)}
              anchorEl={filterAnchorEl}
              onClose={() => setFilterAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Box sx={{ p: 2, width: 320, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#334155' }}>Filters</Typography>
                <TextField
                  size="small"
                  label="Search"
                  placeholder="Vendor name or email"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />

                <TextField
                  size="small"
                  select
                  label="Stage"
                  value={selectedStageId}
                  onChange={(event) => {
                    setSelectedStageId(event.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <MenuItem value="">All Stages</MenuItem>
                  {stageOptions.map((stage: any) => (
                    <MenuItem key={stage.id} value={String(stage.id)}>{stage.name}</MenuItem>
                  ))}
                </TextField>

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setSearchInput('');
                      setSearchQuery('');
                      setSelectedStageId('');
                      setCurrentPage(1);
                      setFilterAnchorEl(null);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Clear
                  </Button>
                  <Button variant="contained" size="small" onClick={() => setFilterAnchorEl(null)} sx={{ textTransform: 'none' }}>
                    Apply
                  </Button>
                </Box>
              </Box>
            </Popover>

            <TableContainer>
              {vendorAnalyticsStatus === STATUS.FAILED && (
                <Error message="Unable to load vendor analytics. Please adjust filters and try again." />
              )}
              <Table sx={{ minWidth: 1850 }}>
                <TableHead sx={{ backgroundColor: '#F7F9FA' }}>
                  <TableRow>
                    {TABLE_COLUMN_CONFIG.map((column) => {
                      const isActiveSort = sortBy === column.sortField;

                      return (
                        <TableCell
                          key={column.label}
                          align={column.align}
                          onClick={() => handleColumnSort(column.sortField)}
                          sx={{
                            ...headerCellSx,
                            minWidth: column.minWidth,
                            cursor: 'pointer',
                            userSelect: 'none',
                          }}
                        >
                          {column.label}
                          <SwapVertIcon
                            fontSize="small"
                            sx={{
                              marginLeft: '4px',
                              verticalAlign: 'middle',
                              opacity: isActiveSort ? 1 : 0.45,
                              transform: isActiveSort && sortOrder === 'desc' ? 'rotate(180deg)' : 'none',
                            }}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length > 0 ? rows.map((row: any, index: number) => {
                    const splitMetrics = getSplitMetrics(row);

                    return (
                      <TableRow
                        key={`${row.vendor_id || row.vendor_name || 'vendor'}-${index}`}
                        sx={{
                          '&:nth-of-type(odd)': { backgroundColor: '#FBFCFF' },
                          '&:hover': { backgroundColor: '#F3F7FF' },
                        }}
                      >
                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{renderValue(row.vendor_name || row.name)}</TableCell>
                        <TableCell align="center" sx={bodyCellSx}>{renderValue(row.total_tasks)}</TableCell>
                        <TableCell align="center" sx={bodyCellSx}>{renderValue(row.completed)}</TableCell>
                        <TableCell align="center" sx={bodyCellSx}>{renderValue(row.pending)}</TableCell>
                        <TableCell align="center" sx={bodyCellSx}>{renderValue(row.active_tasks)}</TableCell>
                        <TableCell align="center" sx={bodyCellSx}>{renderValue(splitMetrics.acceptedAsIsCount)}</TableCell>
                        <TableCell align="center" sx={bodyCellSx}>{renderValue(splitMetrics.acceptedAsIsPercentage)}%</TableCell>
                        <TableCell align="center" sx={bodyCellSx}>{renderValue(splitMetrics.acceptedWithEditsCount)}</TableCell>
                        <TableCell align="center" sx={bodyCellSx}>{renderValue(splitMetrics.acceptedWithEditsPercentage)}%</TableCell>
                        <TableCell align="center" sx={bodyCellSx}>{renderValue(splitMetrics.rejectedCount)}</TableCell>
                        <TableCell align="center" sx={bodyCellSx}>{renderValue(splitMetrics.rejectedPercentage)}%</TableCell>
                        <TableCell align="center" sx={bodyCellSx}>{renderValue(splitMetrics.acceptedTotal)}</TableCell>
                        <TableCell align="center" sx={bodyCellSx}>{renderValue(row.awaiting_review_tasks)}</TableCell>
                        <TableCell align="center" sx={bodyCellSx}>{renderValue(row.completion_percentage)}</TableCell>
                        <TableCell align="center" sx={bodyCellSx}>{renderValue(row.quality_score)}</TableCell>
                      </TableRow>
                    );
                  }) : (
                    <TableRow>
                      <TableCell colSpan={15} align="center">No vendor analytics data found.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_event, page) => setCurrentPage(page)}
                color="primary"
                shape="rounded"
              />
            </Box>

            <TaskStatusModal
              open={openTaskStatusModal}
              onClose={() => setOpenTaskStatusModal(false)}
              taskStatusData={taskStatusOutputJsonResponse?.data}
            />

            <SnackbarNotification
              open={snackbarOpen}
              message={snackbarMessage}
              severity={snackbarSeverity}
              onClose={() => setSnackbarOpen(false)}
            />
          </>
        )
      }
    </>
  )
}

export default VendorAnalytics